import type { CheckRun, DropoffModalProps, MutationVariablesDropOffModal } from '../types'

import { useMutation, useQuery } from '@tanstack/react-query'
import { Checkbox, Col, Modal, notification, Row, Spin, Typography } from 'antd'
import { isAxiosError } from 'axios'
import { parseISO } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'

import $api from '../../../../API'

export function DropoffModal({
  checkRun,
  onSuccess,
  onError,
  onCancel,
  ...props
}: DropoffModalProps): JSX.Element {
  const [emailNotification, setEmailNotification] = useState(false)
  const [selectedReports, setSelectedReports] = useState<string[]>([])

  const query = useQuery({
    queryKey: ['check-runs', checkRun?.id],

    async queryFn({ queryKey, signal }) {
      const [, id] = queryKey

      type CheckRunDetails = Pick<CheckRun, 'id' | 'check_register_reports'>

      const response = await $api.get<CheckRunDetails>(`/check-runs/${id}`, {
        signal
      })

      return response.data
    },

    enabled: typeof checkRun?.id === 'string'
  })

  const mutation = useMutation({
    mutationKey: ['check-runs', checkRun?.id, 'dropoff'],

    async mutationFn({ id, ...payload }: MutationVariablesDropOffModal) {
      try {
        const { data } = await $api.post(`check-runs/${id}/dropoff`, payload)

        return data
      } catch (error) {
        if (isAxiosError(error)) {
          const err = new Error(
            error.response?.data?.message ?? 'Failed to drop off check run'
          ) as Error & { description?: string }

          err.description = error.response?.data?.description
          throw err
        }
        throw error
      }
    }
  })

  const canDropOff = useMemo(() => {
    if (!query.data) {
      return
    }

    const { check_register_reports } = query.data

    return check_register_reports?.length > 0 && check_register_reports.some(report => !!report.set)
  }, [query.data])

  useEffect(() => {
    if (canDropOff !== false) {
      return
    }

    notification.error({
      message: 'Cannot drop off check run',
      description: 'Reports are not grouped by a report set. Try more recent check runs.'
    })

    onCancel?.()
  }, [canDropOff, onCancel])

  const lastReportSet = query.data?.check_register_reports
    .filter(report => !!report.set)
    .sort(
      (a, b) =>
        parseISO(b.set?.created_at ?? '').getTime() - parseISO(a.set?.created_at ?? '').getTime()
    )
    .shift()

  const relevantReports = lastReportSet
    ? (query.data?.check_register_reports.filter(
        report => report.set?.id === lastReportSet.set?.id
      ) ?? [])
    : []

  return (
    <Modal
      {...props}
      okText={mutation.isLoading ? 'Running...' : 'Drop off'}
      okButtonProps={{
        loading: mutation.isLoading,
        disabled: mutation.isLoading || selectedReports.length === 0
      }}
      onOk={() => {
        if (!checkRun) {
          return
        }

        mutation.mutate(
          {
            id: checkRun.id,
            emailNotification,
            reports: selectedReports
          },
          {
            onSuccess() {
              notification.success({
                message: 'Reports dropped off successfully'
              })

              onSuccess?.()
            },

            onError(error) {
              const err = error as Error & { description?: string }
              notification.error({
                message: err.message,
                description: err.description
              })

              onError?.(err)
            }
          }
        )
      }}
      onCancel={() => {
        onCancel?.()
      }}
      afterClose={() => {
        setEmailNotification(false)
        setSelectedReports([])
      }}
    >
      {query.isFetching && (
        <div className='flex justify-center items-center h-32'>
          <Spin />
        </div>
      )}

      {!query.isFetching && (
        <>
          <Typography.Text>
            You are about to drop off latest reports of this check run. The following reports will
            be dropped off:
          </Typography.Text>

          {relevantReports.length > 0 && (
            <div className='flex flex-col gap-2 pt-2'>
              <ul className='list-none'>
                {relevantReports.map(report => {
                  let disabled = false
                  if (
                    report.options &&
                    ((report.options.includeDiagnosisDetails && report.options.includePHI) ||
                      (!report.options.includeDiagnosisDetails && !report.options.includePHI) ||
                      (report.options.includeDiagnosisDetails && !report.options.includePHI))
                  ) {
                    disabled = true
                  }
                  return (
                    <li key={report.id}>
                      <Checkbox
                        disabled={disabled}
                        checked={selectedReports.includes(report.id)}
                        onChange={() => {
                          if (selectedReports.includes(report.id)) {
                            setSelectedReports(reports => reports.filter(val => val !== report.id))
                          } else {
                            setSelectedReports(reports => [...reports, report.id])
                          }
                        }}
                      >
                        {report.name}
                      </Checkbox>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}

          <Checkbox.Group
            className='pt-2'
            value={[...(emailNotification ? ['email-notification'] : [])]}
            onChange={values => {
              setEmailNotification(values.includes('email-notification'))
            }}
          >
            <Row className=''>
              <Col className='flex flex-col gap-1 '>
                <Row className=''>
                  <Col className=''>
                    <Checkbox value='email-notification'>
                      Send email notification to external recipients/clients
                    </Checkbox>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Checkbox.Group>
        </>
      )}
    </Modal>
  )
}
